<template>
	<div>
		<UiButton 
			:label="exportLabel"
			type="secondary"
			prependIcon="excel"
			:sizeIcon="18"
			:block="block"
			size="xs"
			class="ui-export__btn"
			@click="exportTable"
		/>
		<ExportToExcelModal 
			ref="exportToExcelModal" 
			:fileName="sheetTitle"
			:multiple="multiple"
			@changeExcelName="changeExcelName"
		/>
	</div>
</template>

<script>
import ExportToExcelModal from '@components/ExportToExcelModal.vue'
import { getValue } from '@/utils/common';
import { stringSlicer } from '@/utils/common';
import { FORMAT_EXPORT_CODE } from '@utils/format';

export default {
	name: 'UiExportToExcel',
	components: { ExportToExcelModal },
	props: {
		/** Лейбл кнопки экспорта */
		exportLabel: {
			type: String,
			default: 'Экспортировать в Excel',
		},
		/**
		 * Множественный экспорт
		 *
		 * @values true | false
		 */
		multiple: {
			type: Boolean,
			default: false,
		},
		block: {
			type: Boolean,
			default: false,
		},
		/**
		 * Массив с таблицами
		 *
		 * @type Array
		 */
		sheets: {
			type: Array,
			default: () => [],
		},
		/**
		 * Название excel файла после экспорта
		 *
		 * @type String
		 */
		fileName: {
			type: String,
			default: 'table',
		}
	},
	data: () => ({
		sheetsArr: [],
	}),
	computed: {
		sheetTitle() {
			return !this.multiple ? this.sheetsArr[0]?.label || this.fileName : this.fileName;
		},
	},
	methods: {
		formatColumn(XLSX, worksheet, col, fmt) {
			const range = XLSX.utils.decode_range(worksheet['!ref'])
			// range.s.r + 1 пропускает ряд хэдера
			for (let row = range.s.r + 1; row <= range.e.r; ++row) {
				const ref = XLSX.utils.encode_cell({ r: row, c: col })
				if (worksheet[ref] && worksheet[ref].t === 'n') {
					worksheet[ref].z = fmt
				}
			}
		},
		formatToNumItem(item, currency) {
			if (item == null) return null; // null или undefined
			if (typeof item === 'number' && Number.isFinite(item)) {
				// уже число
				return currency === FORMAT_EXPORT_CODE.PERCENT
					? Number(item.toFixed(2)) / 100
					: item;
			}
			if (typeof item !== 'string') return null; // не строка и не число

			const cur_re = /\D*(\d+|\d.*?\d)(?:\D+(\d{2}))?\D*$/;
			const parts = cur_re.exec(item);
			if (!parts) return null; // нет совпадения — значит это не число

			const number = parseFloat(
				parts[1].replace(/\D+/g, '') + '.' + (parts[2] ? parts[2] : '00')
			);
			if (Number.isNaN(number)) return null;

			return currency === FORMAT_EXPORT_CODE.PERCENT
				? Number(number.toFixed(2)) / 100
				: number;
		},

		async exportTable() {
			const XLSX = await import('xlsx');

			this.sheetsArr = JSON.parse(JSON.stringify(this.sheets));
			const modalAnswer = await this.$refs.exportToExcelModal.show();
			if (modalAnswer !== 'export') return;
            
			const workbook = XLSX.utils.book_new();
			const shouldParseAsNumber = (column) =>
				column?.currency || column?.type === 'money' || column?.type === 'number';

			for (let i = 0; i < this.sheets.length; i++) {
				const headers = this.sheets[i].columns.map(column => column.label);
				const data = [];

				this.sheets[i].items.forEach((item) => {
					const newDataArr = [];
					this.sheets[i].columns.forEach((column) => {
						let cell;
						if (typeof column.exportMapper === 'function')      cell = column.exportMapper(item);
						else if (typeof column.mapperItem === 'function')   cell = column.mapperItem(item);
						else if (typeof column.mapper === 'function')       cell = column.mapper(getValue(item, column.name));
						else                                                cell = getValue(item, column.name);

						// к примитиву
						if (cell == null) cell = '';
						else if (Array.isArray(cell)) cell = cell.join('\n');
						else if (typeof cell === 'object') cell = String(cell?.text ?? cell?.value ?? '');

						let valueToPush = cell;

						if (shouldParseAsNumber(column)) {
							const parsed = this.formatToNumItem(cell, column?.currency); // вернёт число или null
							if (typeof parsed === 'number' && Number.isFinite(parsed)) {
								valueToPush = parsed;
							}
						}

						newDataArr.push(valueToPush);
  						
					})
					data.push(newDataArr);
				})
				data.unshift(headers);

				const worksheet = XLSX.utils.aoa_to_sheet(data);

				// форматирование
				for (let col = 0; col < headers.length; col++) {
					const currency = this.sheets[i]?.columns[col].currency || FORMAT_EXPORT_CODE.GENERAL;
					this.formatColumn(XLSX, worksheet, col, currency);
				}

				let sheetName = this.sheetsArr[i].label;
				if (sheetName) {
					// обрезаем слишком длинные слова
					sheetName = sheetName.split(' ').map((string) => string.length > 4 ? string.slice(0, 4) : string).join('.');
					// обрезаем по максимальной длине в 31 символ
					sheetName = stringSlicer(sheetName, 28);
				} else {
					sheetName = this.fileName;
				}

				XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
			}

			const exportName = !this.multiple ? this.sheetsArr[0].label || this.fileName : this.fileName;
			XLSX.writeFile(workbook, exportName + ".xlsx");
		},
		changeExcelName(val) {
			if (!this.multiple) this.sheetsArr[0].label = val;
		},
	} 
}
</script>
